import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  translationKey?: string;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb navigation component
 *
 * Provides hierarchical navigation with:
 * - Semantic HTML with <nav> and structured list
 * - Accessibility with aria-label and current page indication
 * - Schema.org BreadcrumbList JSON-LD structured data
 * - Responsive design (hidden on mobile by default)
 * - i18n support for labels
 *
 * @example
 * <Breadcrumb items={[
 *   { label: 'Inicio', href: '/', translationKey: 'common.home' },
 *   { label: 'Blog', href: '/blog' },
 *   { label: 'Post Title' }
 * ]} />
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className = '' }) => {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return null;
  }

  // Generate schema.org BreadcrumbList JSON-LD
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.translationKey ? t(item.translationKey) : item.label,
      ...(item.href && { item: `${window.location.origin}${item.href}` }),
    })),
  };

  return (
    <>
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Visual breadcrumb navigation */}
      <nav
        aria-label="Breadcrumb"
        className={`hidden md:block mb-6 ${className}`}
      >
        <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const label = item.translationKey ? t(item.translationKey) : item.label;
            const isHome = index === 0;

            return (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground/50" />
                )}

                {isLast ? (
                  // Current page - not a link
                  <span
                    className="font-medium text-foreground truncate max-w-[200px] md:max-w-[300px]"
                    aria-current="page"
                  >
                    {isHome && <Home className="h-4 w-4 inline mr-1" />}
                    {label}
                  </span>
                ) : (
                  // Linked breadcrumb item
                  <Link
                    to={item.href || '/'}
                    className="hover:text-foreground transition-colors truncate max-w-[150px] md:max-w-[200px] flex items-center"
                  >
                    {isHome && <Home className="h-4 w-4 mr-1" />}
                    {label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

/**
 * Hook to generate breadcrumb items from current location
 *
 * @example
 * const breadcrumbs = useBreadcrumbs([
 *   { label: 'Inicio', href: '/' },
 *   { label: 'Blog', href: '/blog' },
 *   { label: post.title }
 * ]);
 */
export const useBreadcrumbs = (items: BreadcrumbItem[]) => {
  // Add home automatically if not present
  if (items.length > 0 && items[0].href !== '/') {
    return [
      { label: 'Inicio', href: '/', translationKey: 'common.home' },
      ...items,
    ];
  }
  return items;
};

export default Breadcrumb;
